import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import { getToken } from '../services/authService';
import { clearCart } from '../services/cartService';
import API_BASE_URL from '../config';
import toast from 'react-hot-toast';
import { UpcPayment } from 'upc-payment-js';
import { albanianDivisions } from '../data/albanianLocations';
import { useCurrency } from '../context/CurrencyContext';
import { getShopLocations } from '../services/shopLocationService';
import { getCategories } from '../services/categoryService';
import { getProductById } from '../services/productService';
import { priceInfo } from '../utils/priceUtils';
import { getTradeInCampaign, evaluateItemCampaign } from '../services/campaignService';


export default function Checkout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { currency, rates, conversionNotice } = useCurrency(); // kept if we need to show totals
  const isForeignCurrency = currency === 'EUR' || currency === 'USD';
  const noticeText = conversionNotice || 'Konvertimi ne EUR/USD eshte vetem informes. Fatura do te leshohet ne Leke';

  // Form State
  const [email, setEmail] = useState('');
  const [pickupMethod, setPickupMethod] = useState('address'); // 'store' or 'address'
  const [country, setCountry] = useState('ALBANIA');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [company, setCompany] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [region, setRegion] = useState('');
  const [zip, setZip] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [shopLocations, setShopLocations] = useState([]);
  const [selectedShopId, setSelectedShopId] = useState(null);
  const [iuteConfig, setIuteConfig] = useState({ enabled: false, publicKey: '' });
  const [oldProduct, setOldProduct] = useState('');
  const [otnDiscount, setOtnDiscount] = useState(0); // e.g. 25 = 25%
  const [isOTN, setIsOTN] = useState(false);
  const [cartItems, setCartItems] = useState([]);
  const [tradeInCampaign, setTradeInCampaign] = useState(null);

  useEffect(() => {
    let mounted = true;
    Promise.all([
      import('../services/cartService').then(m => m.getCart()).catch(() => []),
      getTradeInCampaign().catch(() => null)
    ]).then(([cItems, camp]) => {
      if (mounted) {
        setCartItems(Array.isArray(cItems) ? cItems : []);
        setTradeInCampaign(camp);
      }
    });
    return () => { mounted = false; };
  }, []);

  const hasTradeInDeclared = useMemo(() => {
    return (cartItems || []).some(it => Boolean(it.tradeIn || it.hasTradeIn));
  }, [cartItems]);

  useEffect(() => {
    const fetchIuteConfig = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/payment/iute-config`);
        if (res.ok) {
          const data = await res.json();
          setIuteConfig(data);
          if (data.enabled && data.publicKey) {
            if (!document.getElementById('iute-js')) {
              const script = document.createElement('script');
              script.id = 'iute-js';
              script.type = 'text/javascript';
              script.src = 'https://ecom.iutecredit.al/iutepay.js';
              script.onload = () => {
                if (window.iute) window.iute.configure(data.publicKey, 'sq');
              };
              document.head.appendChild(script);

              const link = document.createElement('link');
              link.id = 'iute-css';
              link.rel = 'stylesheet';
              link.type = 'text/css';
              link.href = 'https://ecom.iutecredit.al/iutepay.css';
              document.head.appendChild(link);
            }
          }
        }
      } catch (err) {
        console.error('Failed to fetch Iute configuration.', err);
      }
    };
    fetchIuteConfig();
  }, []);


  useEffect(() => {
    const query = new URLSearchParams(location.search);
    const status = query.get('status');
    if (status === 'success') {
      clearCart();
      // Card routing happens within the handleComplete flow, so this standalone useEffect
      // from top-level location.search may be vestigial, but we'll leave it in place
      toast.success('Pagesa u krye me sukses! Faleminderit.');
      navigate('/');
    } else if (status === 'failure') {
      toast.error('Pagesa dështoi ose u anulua.');
    }
  }, [location.search, navigate]);

  useEffect(() => {
    const loadShops = async () => {
      try {
        const data = await getShopLocations();
        setShopLocations(data);
      } catch (err) {
        console.error('Failed to load shop locations', err);
      }
    };
    loadShops();
  }, []);

  // --- Read OldToNew product from sessionStorage ---
  const clearOTNSession = () => {
    try {
      sessionStorage.removeItem('glb_old_product');
      sessionStorage.removeItem('glb_from_old_to_new');
      sessionStorage.removeItem('glb_otn_discount');
      sessionStorage.removeItem('glb_otn_category_id');
    } catch (_) { }
  };

  useEffect(() => {
    try {
      const otnFlag = sessionStorage.getItem('glb_from_old_to_new') === 'true';
      const saved = sessionStorage.getItem('glb_old_product');
      if (saved) setOldProduct(saved);
      if (otnFlag) {
        setIsOTN(true);
        const pct = parseInt(sessionStorage.getItem('glb_otn_discount') || '0', 10);
        setOtnDiscount(isNaN(pct) ? 0 : pct);
      }
    } catch (_) { }
  }, []);

  // --- Pre-fill Logged-in User Data ---
  useEffect(() => {
    const fetchUserProfile = async () => {
      const token = getToken();
      if (!token) return;
      try {
        const res = await fetch(`${API_BASE_URL}/users/me`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const user = await res.json();
          if (user.name) setFirstName(user.name);
          if (user.surname) setLastName(user.surname);
          if (user.email) setEmail(user.email);
          if (user.phoneNumber) setPhone(user.phoneNumber);
          if (user.country) setCountry(user.country);
          if (user.streetAddress) setAddress(user.streetAddress);
          if (user.zip) setZip(user.zip);

          if (user.city) {
            setCity(user.city);
            // Auto-detect region from city if stateProvince is not explicitly set in the user profile
            if (user.stateProvince) {
              setRegion(user.stateProvince);
            } else {
              const foundRegion = Object.keys(albanianDivisions).find(r => albanianDivisions[r].includes(user.city));
              if (foundRegion) setRegion(foundRegion);
            }
          } else if (user.stateProvince) {
            setRegion(user.stateProvince);
          }
        }
      } catch (err) {
        console.error('Failed to fetch user profile for checkout pre-fill', err);
      }
    };
    fetchUserProfile();
  }, []);

  // --- Region/City Logic ---
  const regionsList = useMemo(() => Object.keys(albanianDivisions).sort(), []);

  const availableCities = useMemo(() => {
    if (region && albanianDivisions[region]) {
      return albanianDivisions[region].sort();
    }
    return Object.values(albanianDivisions).flat().sort();
  }, [region]);

  const handleRegionChange = (e) => {
    const newRegion = e.target.value;
    setRegion(newRegion);
    setCity('');
  };

  const handleCityChange = (e) => {
    const newCity = e.target.value;
    setCity(newCity);
    if (!region && newCity) {
      const foundRegion = Object.keys(albanianDivisions).find(r => albanianDivisions[r].includes(newCity));
      if (foundRegion) setRegion(foundRegion);
    }
  };

  // -------------------------

  const handleComplete = async () => {
    // Basic validation
    const isStore = pickupMethod === 'store';
    if (!email || !firstName || !lastName || !phone) {
      toast.error('Ju lutem plotësoni të gjitha fushat e kontaktit.');
      return;
    }

    if (isStore && !selectedShopId) {
      toast.error('Ju lutem zgjidhni një dyqan për tërheqje.');
      return;
    }

    if (!isStore && (!address || !city)) {
      toast.error('Ju lutem plotësoni adresën dhe qytetin.');
      return;
    }

    try {
      const items = await (async () => {
        const { getCart } = await import('../services/cartService');
        return await getCart();
      })();

      // Load campaign, category tree & product details for trade-in matching & offer price resolution
      const [campaign, categoryTree, prodDetailsList] = await Promise.all([
        getTradeInCampaign().catch(() => null),
        getCategories().catch(() => []),
        Promise.all(items.map(it => getProductById(it.id).catch(() => null)))
      ]);
      const productMap = {};
      (prodDetailsList || []).forEach(p => { if (p && p.id) productMap[p.id] = p; });

      const getEffectiveItemPrice = (it) => {
        const p = productMap[it.id];
        const servicesSum = Array.isArray(it.services)
          ? it.services.reduce((ss, sv) => ss + Number((sv && sv.price) || 0), 0)
          : 0;

        if (it.isBundle) {
          return { effectivePrice: Number(it.price || 0), originalPrice: Number(it.price || 0), servicesSum: 0 };
        }

        if (p) {
          const info = priceInfo(p);
          return { effectivePrice: info.display, originalPrice: info.original, servicesSum };
        }

        const basePrice = it.productPrice !== undefined && it.productPrice !== null
          ? Number(it.productPrice)
          : Number(it.price || 0) - servicesSum;

        return { effectivePrice: basePrice, originalPrice: basePrice, servicesSum };
      };

      const finalPayloadItems = items.map(it => {
        const ev = evaluateItemCampaign({
          item: it,
          campaign,
          product: productMap[it.id],
          categoryTree
        });
        const isCampaignItem = Boolean(ev.isEligible && !ev.isExcluded);
        const isTradeIn = Boolean(it.tradeIn && isCampaignItem);
        const { effectivePrice, originalPrice, servicesSum } = getEffectiveItemPrice(it);

        let unitPrice;
        if (isCampaignItem) {
          unitPrice = isTradeIn ? ev.unitFullPriceWithServices : ev.unitBasePriceWithServices;
        } else {
          unitPrice = effectivePrice + servicesSum;
        }

        const origUnit = isCampaignItem ? (ev.unitInitialPrice + servicesSum) : (originalPrice + servicesSum);

        return {
          ...it,
          price: unitPrice,
          productPrice: isCampaignItem
            ? (isTradeIn ? ev.unitFullDiscountedPrice : ev.unitBaseDiscountedPrice)
            : effectivePrice,
          originalPrice: (origUnit && origUnit > unitPrice) ? origUnit : null,
          hasTradeIn: isTradeIn,
          tradeInDiscount: isTradeIn ? ev.fullDiscount : (isCampaignItem ? ev.baseDiscount : 0),
          tradeInDetails: isTradeIn ? {
            campaignName: campaign?.name || 'Sill të vjetrën, merr të renë',
            baseDiscount: ev.baseDiscount,
            fullDiscount: ev.fullDiscount,
            withOldDevice: true
          } : (isCampaignItem ? {
            campaignName: campaign?.name || 'Sill të vjetrën, merr të renë',
            baseDiscount: ev.baseDiscount,
            fullDiscount: ev.fullDiscount,
            withOldDevice: false
          } : null)
        };
      });

      const discountedTotal = finalPayloadItems.reduce((s, it) => s + Number(it.price) * Number(it.qty || 1), 0);
      const hasAnyTradeIn = finalPayloadItems.some(it => it.hasTradeIn);
      const tradeInSuffix = hasAnyTradeIn ? ' | [Fushata: Sill të vjetrën merr të renë - Pajisje e vjetër për dorëzim]' : '';

      const savedOldProduct = isOTN
        ? sessionStorage.getItem('glb_old_product')
        : null;

      const orderPayload = {
        firstName, lastName, email, phone, company, country,
        address: isStore
          ? (savedOldProduct ? `Marrje në dyqan | [Produkti i vjetër: ${savedOldProduct}]${tradeInSuffix}` : `Marrje në dyqan${tradeInSuffix}`)
          : (savedOldProduct ? `${address} | [Produkti i vjetër: ${savedOldProduct}]${tradeInSuffix}` : `${address}${tradeInSuffix}`),
        city: isStore ? '—' : city,
        region: isStore ? '—' : region,
        zip: isStore ? '—' : zip,
        pickupMethod, paymentMethod,
        selectedShopId: isStore ? selectedShopId : null,
        oldProduct: savedOldProduct || null,
        items: finalPayloadItems,
        total: discountedTotal,
        currency,
        exchangeRate: rates[currency] || 1
      };

      const token = getToken();
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const res = await fetch(`${API_BASE_URL}/orders`, {
        method: 'POST',
        headers,
        body: JSON.stringify(orderPayload)
      });

      if (!res.ok) throw new Error('Failed to create order');
      const orderData = await res.json();
      const orderId = orderData.id;

      if (paymentMethod === 'card') {
        // Determine which gateway is currently active (respects the day-based schedule)
        let activeGateway = 'upc';
        try {
          const gwRes = await fetch(`${API_BASE_URL}/payment/active-gateway`);
          if (gwRes.ok) {
            const gwData = await gwRes.json();
            activeGateway = gwData.activeGateway || 'upc';
          }
        } catch (_) { /* fallback to upc */ }

        if (activeGateway === 'credins') {
          // ---- CREDINS / SIA VPOS REDIRECT FLOW ----
          // Call credins-initiate to get signed form fields, then POST to bank gateway
          const initiateRes = await fetch(`${API_BASE_URL}/payment/credins-initiate`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { 'Authorization': `Bearer ${token}` } : {})
            },
            body: JSON.stringify({ orderId })
          });

          if (!initiateRes.ok) {
            const errData = await initiateRes.json();
            throw new Error(errData.error || 'Credins payment initiation failed');
          }

          const { actionUrl, fields } = await initiateRes.json();

          // Build a hidden form and auto-submit to the Credins gateway (browser redirect)
          const form = document.createElement('form');
          form.method = 'POST';
          form.action = actionUrl;
          form.style.display = 'none';

          Object.entries(fields).forEach(([name, value]) => {
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = name;
            input.value = value;
            form.appendChild(input);
          });

          clearOTNSession();
          document.body.appendChild(form);
          form.submit();
          return; // Browser will redirect; execution ends here
        }

        // ---- UPC SDK IFRAME FLOW (unchanged) ----
        const signRes = await fetch(`${API_BASE_URL}/payment/sign-js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {})
          },
          body: JSON.stringify({
            orderId,
            customer: { email, firstName, lastName }
          })
        });

        if (!signRes.ok) {
          const errData = await signRes.json();
          throw new Error(errData.error || 'Payment signing failed');
        }

        const { merchant, payment, customer: outCustomer } = await signRes.json();

        try {
          let handleMessage;
          const upc = new UpcPayment({
            mode: 'PaymentModalIframe',
            merchant: {
              id: merchant.id,
              terminalId: merchant.terminalId,
              signature: merchant.signature,
            },
            customer: outCustomer || { email, firstName, lastName },
            iframeProps: {
              wrapperSelector: 'body',
              callback: (data) => {
                console.log('UPC SDK Callback:', data);
                if (data && (data.event === 'success' || data.event === 'failure')) {
                  const wrapper = document.querySelector('.upc-payment-iframe-wrapper');
                  if (wrapper) wrapper.remove();
                  if (handleMessage) window.removeEventListener('message', handleMessage);
                  clearOTNSession();
                  navigate(`/order-result?status=${data.event}&method=card&orderId=${orderId}`);
                }
              }
            }
          });

          // Add a global listener for our custom redirect message
          handleMessage = (event) => {
            if (event.data && event.data.from === 'UpcPaymentIframe' && event.data.message === 'CustomRedirect') {
              console.log('Intercepted UPC Redirect:', event.data.url);

              // Close the modal
              const wrapper = document.querySelector('.upc-payment-iframe-wrapper');
              if (wrapper) wrapper.remove();

              window.removeEventListener('message', handleMessage);
              clearOTNSession();

              const resolvedOrderId = event.data.orderId || orderId;
              // Navigate to local result page
              navigate(`/order-result?status=${event.data.status}&method=card&orderId=${resolvedOrderId}`);
            }
          };
          window.addEventListener('message', handleMessage);

          upc.pay(payment);
          return;
        } catch (sdkErr) {
          console.error('UPC SDK Error:', sdkErr);
          toast.error('Dështoi nisja e modulit të pagesave. Provoni përsëri.');
        }
      }

      if (paymentMethod === 'iute') {
        if (!iuteConfig.publicKey) {
          toast.error('Iute Pay është aktivizuar në Admin, por API Key (Public Key) mungon! Ju lutem vendoseni atë në konfigurime.');
          return;
        }
        if (!window.iute) {
          toast.error('Skripti i IutePay nuk u ngarkua. Kontrolloni bllokuesit e reklamave ose rimbushni faqen.');
          return;
        }
        const formattedItems = items.map(it => {
          let finalUnitPrice = 0;
          if (it.isBundle) {
            finalUnitPrice = Number(it.price || 0);
          } else {
            const servicesSum = Array.isArray(it.services) ? it.services.reduce((ss, sv) => ss + Number((sv && sv.price) || 0), 0) : 0;
            const productPrice = (it.productPrice !== undefined && it.productPrice !== null) ? Number(it.productPrice) : (Number(it.price || 0) - servicesSum);
            finalUnitPrice = productPrice + servicesSum;
          }

          const rawImg = (it.image && typeof it.image === 'string' && it.image.trim()) ? it.image.trim() : '/logo192.png';
          const fullImgUrl = rawImg.startsWith('http') ? rawImg : `${window.location.origin}${rawImg.startsWith('/') ? '' : '/'}${rawImg}`;
          const fullProdUrl = `${window.location.origin}/product/${encodeURIComponent(it.slug || it.id || 'item')}`;

          return {
            displayName: (it.title || it.name || 'Produkti').slice(0, 100),
            sku: (it.id || 'N/A').toString(),
            unitPrice: finalUnitPrice,
            qty: Number(it.qty || 1),
            itemImageUrl: fullImgUrl,
            itemUrl: fullProdUrl
          };
        });

        const toAbsoluteUrl = (path) => {
          if (!path) return '';
          if (/^https?:\/\//i.test(path)) return path;
          const origin = window.location.origin.replace(/\/+$/, '');
          const cleanPath = path.startsWith('/') ? path : `/${path}`;
          return `${origin}${cleanPath}`;
        };

        const payload = {
          merchant: {
            userConfirmationUrl: toAbsoluteUrl(`${API_BASE_URL}/payment/iute-confirm?orderId=${orderId}`),
            userCancelUrl: toAbsoluteUrl(`${API_BASE_URL}/payment/iute-cancel?orderId=${orderId}`),
            userConfirmationUrlAction: "POST",
            name: "Globe Albania"
          },
          shipping: {
            name: { first: firstName, last: lastName },
            address: { line1: address || 'N/A', city: city || 'N/A', zipcode: zip || '0000', country: 'alb' },
            phoneNumber: phone,
            email: email
          },
          billing: {
            name: { first: firstName, last: lastName },
            address: { line1: address || 'N/A', city: city || 'N/A', zipcode: zip || '0000', country: 'alb' },
            phoneNumber: phone,
            email: email
          },
          items: formattedItems,
          orderId: orderId.toString(),
          currency: 'ALL',
          shippingAmount: 0,
          taxAmount: 0,
          subtotal: discountedTotal,
          total: discountedTotal
        };

        window.iute.checkout(payload, {
          onSuccess: function (result) {
            clearOTNSession();
            clearCart();
            navigate(`/order-result?status=success&method=iute&orderId=${orderId}`);
          },
          onFailure: function (result) {
            toast.error(result.message || 'Pagesa me Iute u anulua ose dështoi.');
          }
        });
        return;
      }

      if (paymentMethod === 'bank') {
        // For bank transfer, skip the gateway and go directly to success page
        clearOTNSession();
        await clearCart();
        navigate('/order-result?status=success&method=bank');
        return;
      }

      // Normal flow for Cash/Other
      clearOTNSession();
      await clearCart();
      // Navigate to result page for cash rather than home page
      navigate('/order-result?status=success&method=cash');
    } catch (err) {
      console.error(err);
      toast.error('Dështoi vendosja e porosisë: ' + err.message);
    }
  };

  // Removal of guest checkout gate logic
  /*
  if (!isLoggedIn && !guestMode) {
      return (
          <div className="page-container" style={{ maxWidth: 800, margin: '40px auto', padding: '20px', textAlign: 'center' }}>
              <h2>Checkout</h2>
              <p style={{ marginBottom: 30 }}>Please login to continue or proceed as a guest.</p>
              <div className="checkout-actions" style={{ display: 'flex', justifyContent: 'center', gap: 20 }}>
                  <button
                      onClick={() => navigate('/login')}
                      style={{ padding: '12px 24px', background: '#0b79d0', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 16 }}
                  >
                      Log In
                  </button>
                  <button
                      onClick={() => setGuestMode(true)}
                      style={{ padding: '12px 24px', background: 'transparent', border: '1px solid #0b79d0', color: '#0b79d0', borderRadius: 4, cursor: 'pointer', fontSize: 16 }}
                  >
                      Continue as Guest
                  </button>
              </div>
          </div>
      );
  }
  */

  // Styles matching the image roughly
  const sectionTitleStyle = {
    color: '#0b79d0',
    fontSize: '18px',
    fontWeight: '600',
    marginBottom: '10px',
    marginTop: '20px',
    borderBottom: '1px solid #eee',
    paddingBottom: '5px'
  };

  const inputStyle = {
    width: '100%',
    padding: '10px',
    border: '1px solid #abd1e6',
    borderRadius: '20px',
    outline: 'none',
    fontSize: '14px',
    color: '#555'
  };

  const labelStyle = {
    display: 'block',
    fontSize: '13px',
    fontWeight: '600',
    marginBottom: '5px',
    color: '#333'
  };

  return (
    <div className="page-container" style={{ maxWidth: 700, margin: '0 auto', padding: '20px 20px 60px' }}>

      {/* Informacioni i kontaktit */}
      <div style={sectionTitleStyle}>Informacioni i kontaktit</div>
      <div style={{ marginBottom: 15 }}>
        <label style={labelStyle}>E-mail</label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          style={inputStyle}
        />
      </div>

      {/* Delivery Method */}
      <div style={sectionTitleStyle}>Metoda e tërheqjes së produktit</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
        <div style={{
          display: 'flex',
          border: '1px solid #abd1e6',
          borderRadius: '20px',
          overflow: 'hidden',
          padding: '2px'
        }}>
          <button
            onClick={() => setPickupMethod('address')}
            style={{
              background: pickupMethod === 'address' ? '#26a7df' : 'transparent',
              border: 'none',
              padding: '8px 20px',
              borderRadius: '18px',
              cursor: 'pointer',
              fontWeight: pickupMethod === 'address' ? 'bold' : 'normal',
              color: pickupMethod === 'address' ? '#fff' : '#047ba8',
              display: 'flex',
              alignItems: 'center',
              gap: 5,
            }}
          >
            {pickupMethod === 'address' && <span></span>}
            Në adresë
          </button>
          <button
            onClick={() => setPickupMethod('store')}
            style={{
              background: pickupMethod === 'store' ? '#26a7df' : 'transparent',
              border: 'none',
              padding: '8px 20px',
              borderRadius: '18px',
              cursor: 'pointer',
              fontWeight: pickupMethod === 'store' ? 'bold' : 'normal',
              color: pickupMethod === 'store' ? '#fff' : '#047ba8',
              display: 'flex',
              alignItems: 'center',
              gap: 5
            }}
          >
            {pickupMethod === 'store' && <span></span>} Në dyqan
          </button>
        </div>
      </div>

      {/* Buyer Information / Shipping Information */}
      <div style={sectionTitleStyle}>
        {pickupMethod === 'store' ? 'Blerësi' : 'Informacioni i dërgesës'}
      </div>

      {/* Old product pre-fill from OldToNew module */}
      {oldProduct && (
        <div style={{ marginBottom: 15, padding: '10px 18px', background: '#f0f9ff', border: '1.5px solid #abd1e6', borderRadius: '12px', display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 13, color: '#047ba8' }}>
            <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" style={{ verticalAlign: 'middle', marginRight: 4 }}>
              <path d="M20 7H9a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h11a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2z" />
              <path d="M16 3H5a2 2 0 0 0-2 2v10" />
            </svg>
            Produkti i vjetër:
          </span>
          <span style={{ fontWeight: '700', fontSize: 14, color: '#003e4e', flex: 1 }}>{oldProduct}</span>
        </div>
      )}

      {/* OTN Discount Banner */}
      {isOTN && otnDiscount > 0 && (
        <div style={{ marginBottom: 15, padding: '12px 18px', background: '#f0fdf4', border: '1.5px solid #4ade80', borderRadius: '12px', display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, borderRadius: '50%', background: '#dcfce7', color: '#15803d', flexShrink: 0, fontSize: 18 }}>
            ♻️
          </span>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#166534' }}>
              Zbritje {otnDiscount}% — Oferta "Nga e Vjetra në të Re"
            </div>
            <div style={{ fontSize: 12, color: '#15803d' }}>
              Çmimi i produktit është zbritu me {otnDiscount}% bazuar në kategorinë tuaj.
            </div>
          </div>
        </div>
      )}

      {pickupMethod === 'address' && (
        <div style={{ marginBottom: 15 }}>
          <label style={labelStyle}>Shteti</label>
          <select
            value={country}
            onChange={e => setCountry(e.target.value)}
            style={{ ...inputStyle, appearance: 'none', background: '#fff' }}
          >
            <option value="ALBANIA">ALBANIA</option>
          </select>
        </div>
      )}

      <div className="checkout-row" style={{ display: 'flex', gap: 20, marginBottom: 15 }}>
        <div style={{ flex: 1 }}>
          <label style={labelStyle}>Emri</label>
          <input
            type="text"
            value={firstName}
            onChange={e => setFirstName(e.target.value)}
            style={inputStyle}
          />
        </div>
        <div style={{ flex: 1 }}>
          <label style={labelStyle}>Mbiemri</label>
          <input
            type="text"
            value={lastName}
            onChange={e => setLastName(e.target.value)}
            style={inputStyle}
          />
        </div>
      </div>

      <div style={{ marginBottom: 15 }}>
        <label style={labelStyle}>Nr. telefonit</label>
        <input
          type="tel"
          value={phone}
          onChange={e => setPhone(e.target.value)}
          style={inputStyle}
        />
      </div>

      <div style={{ marginBottom: 15 }}>
        <label style={labelStyle}>Kompania</label>
        <input
          type="text"
          value={company}
          onChange={e => setCompany(e.target.value)}
          style={inputStyle}
        />
      </div>

      {/* Shop Selection (only if store pickup) */}
      {pickupMethod === 'store' && (
        <div style={{ marginTop: '20px', marginBottom: '30px' }}>
          <div style={{ ...labelStyle, marginBottom: '10px' }}>Zgjidhni dyqanin për tërheqje:</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {shopLocations.length === 0 ? (
              <p style={{ fontSize: '14px', color: '#666' }}>Nuk u gjet asnjë dyqan i disponueshëm.</p>
            ) : (
              shopLocations.map(shop => (
                <div
                  key={shop.id}
                  onClick={() => setSelectedShopId(shop.id)}
                  style={{
                    border: `1.5px solid ${selectedShopId === shop.id ? '#047ba8' : '#abd1e6'}`,
                    borderRadius: '12px',
                    padding: '12px 15px',
                    cursor: 'pointer',
                    background: selectedShopId === shop.id ? '#f0f9ff' : '#fff',
                    transition: 'all 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                  }}
                >
                  <div style={{
                    width: 18,
                    height: 18,
                    borderRadius: '50%',
                    border: '2px solid #abd1e6',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: selectedShopId === shop.id ? '#047ba8' : 'transparent',
                    borderColor: selectedShopId === shop.id ? '#047ba8' : '#abd1e6'
                  }}>
                    {selectedShopId === shop.id && <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#fff' }}></div>}
                  </div>
                  <div style={{ fontSize: '14px', color: '#333' }}>
                    <span style={{ fontWeight: 'bold' }}>{shop.name}</span>
                    <span style={{ margin: '0 8px', color: '#ccc' }}>|</span>
                    <span style={{ color: '#666' }}>{shop.address}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {pickupMethod === 'address' && (
        <>
          <div style={{ marginBottom: 15 }}>
            <label style={labelStyle}>Adresa</label>
            <textarea
              value={address}
              onChange={e => setAddress(e.target.value)}
              style={{ ...inputStyle, resize: 'none', height: '80px' }}
            />
          </div>

          <div className="checkout-row" style={{ display: 'flex', gap: 20, marginBottom: 20 }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Qarku</label>
              <select value={region} onChange={handleRegionChange} style={inputStyle}>
                <option value="">Select...</option>
                {regionsList.map(r => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Qyteti</label>
              <select value={city} onChange={handleCityChange} style={inputStyle}>
                <option value="">Select...</option>
                {availableCities.map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Kodi postar</label>
              <input
                type="text"
                value={zip}
                onChange={e => setZip(e.target.value)}
                style={inputStyle}
              />
            </div>
          </div>
        </>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 30 }}>
      </div>

      {/* Foreign Currency Conversion Notice (EUR / USD) */}
      {isForeignCurrency && noticeText && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '10px 16px',
            marginBottom: 20,
            background: '#fffbeb',
            border: '1px solid #fde68a',
            borderRadius: 8,
            color: '#92400e',
            fontSize: 13,
            fontWeight: 500,
            lineHeight: 1.4,
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <span>{noticeText}</span>
        </div>
      )}

      {/* Payment Method */}
      <div style={sectionTitleStyle}>Metoda e pagesës</div>

      {/* Info notice for trade-in */}
      {hasTradeInDeclared && tradeInCampaign?.enabled && (
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 12,
            padding: '14px 16px',
            marginBottom: 20,
            background: '#f0fdf4',
            border: '1.5px solid #86efac',
            borderRadius: 12,
            color: '#166534',
            fontSize: 13.5,
            lineHeight: 1.45,
          }}
        >
          <span style={{ fontSize: 20, flexShrink: 0, marginTop: 1 }}>♻️</span>
          <div>
            <div style={{ fontWeight: 700, color: '#15803d', fontSize: 13.5 }}>
              Fushata "Sill të vjetrën, merr të renë"
            </div>
            <div style={{ marginTop: 3, color: '#166534', fontWeight: 600 }}>
              {tradeInCampaign?.paymentNotice || "Keni deklaruar se do të dorëzoni pajisjen e vjetër. Nëse nuk e dorëzoni, çmimi kthehet në uljen bazë."}
            </div>
          </div>
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 30 }}>
        {[
          { id: 'card', label: 'ME KARTË' },
          { id: 'bank', label: 'ME BANKË' },
          { id: 'cash', label: 'CASH' },
          ...(iuteConfig.enabled ? [{ id: 'iute', label: 'IUTE' }] : [])
        ].map(method => (
          <label key={method.id} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
            <div
              onClick={() => setPaymentMethod(method.id)}
              style={{
                width: 20,
                height: 20,
                borderRadius: '50%',
                border: '1px solid #abd1e6',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: paymentMethod === method.id ? '#abd1e6' : 'transparent'
              }}
            >
              {paymentMethod === method.id && <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#fff' }}></div>}
            </div>
            <span style={{ fontSize: 13, fontWeight: '600', display: 'flex', alignItems: 'center', gap: 8 }}>
              {method.label}
            </span>
          </label>
        ))}
      </div>

      {/* Actions */}
      <div className="checkout-actions" style={{ display: 'flex', alignItems: 'center', gap: 20, marginTop: 40 }}>
        <button
          onClick={handleComplete}
          style={{
            background: '#047ba8',
            color: '#fff',
            border: 'none',
            borderRadius: '20px',
            padding: '12px 30px',
            fontSize: '14px',
            fontWeight: 'bold',
            cursor: 'pointer'
          }}
        >
          Kompleto
        </button>
        <button
          onClick={() => navigate(-1)}
          className="checkout-back-button"
          style={{
            background: 'transparent',
            color: '#333',
            border: 'none',
            fontSize: '13px',
            cursor: 'pointer',
            textDecoration: 'underline'
          }}
        >
          Shko mbrapa
        </button>
      </div>

    </div>
  );
}
